/**
 * Scroll the app's ACTUAL scrolling element back to the top.
 *
 * WHY THIS EXISTS
 *
 * `window.scrollTo(0, 0)` scrolls the DOCUMENT. That only does something when the document is
 * what overflows — and in this app it isn't, in two different ways:
 *
 *   1. `.app-container` is `height: 100vh; overflow: hidden` (styles/layout.css), with the navbar
 *      taking its height off the top and `.main-content` taking the rest and scrolling internally.
 *      So for a normal page the scrollbar belongs to `.main-content`, and the document has none.
 *
 *   2. Ten pages additionally render `.std-pg-main-area`, which is `position: fixed` at 100vh with
 *      its own `overflow-y: scroll` (index.css). Those pages scroll inside THAT box, and always
 *      have — `window.scrollTo` was already inert there long before the layout change.
 *
 * Either way the call silently succeeds and moves nothing, which is the worst failure mode: no
 * error, no scroll, and nothing to grep for.
 *
 * Ordered innermost-outward on purpose. A page that owns `.std-pg-main-area` has its content
 * inside that box, so scrolling `.main-content` around it would move the wrong thing — the page
 * would sit still while its container twitched.
 *
 * @param {ScrollToOptions|undefined} options e.g. `{ behavior: 'smooth' }`. `top: 0` is implied.
 */
export function scrollAppToTop(options = {}) {
  const opts = { top: 0, left: 0, ...options };

  // Innermost first: a page-owned scroll box, if this page has one.
  const pageBox = document.querySelector('.std-pg-main-area');
  if (pageBox && pageBox.scrollHeight > pageBox.clientHeight) {
    pageBox.scrollTo(opts);
    return;
  }

  // Otherwise the layout's scroll container.
  const main = document.querySelector('.main-content');
  if (main) {
    main.scrollTo(opts);
    return;
  }

  // Last resort — a context without the Layout wrapper (a demo route, a test harness), where the
  // document really is what scrolls.
  window.scrollTo(opts);
}

export default scrollAppToTop;
